const getNavigatorObject = (targetWindow = window) =>
  targetWindow?.navigator || (typeof navigator !== "undefined" ? navigator : undefined);

export const isTouchDevice = (targetWindow = window) => {
  if (!targetWindow || typeof targetWindow.matchMedia !== "function") {
    return false;
  }

  const navigatorObject = getNavigatorObject(targetWindow);
  return (
    targetWindow.matchMedia("(pointer: coarse)").matches ||
    Number(navigatorObject?.maxTouchPoints || 0) > 0
  );
};

export const isProbablyIpad = (targetWindow = window) => {
  const navigatorObject = getNavigatorObject(targetWindow);
  const platform = String(navigatorObject?.platform || "");
  const userAgent = String(navigatorObject?.userAgent || "");
  const touchPoints = Number(navigatorObject?.maxTouchPoints || 0);

  return (
    /iPad/i.test(userAgent) ||
    /iPad/i.test(platform) ||
    // Desktop-mode iPad UA also appears in emulators that retain the host
    // platform and expose only one touch point.
    ((platform === "MacIntel" || /Macintosh/i.test(userAgent)) && touchPoints > 0)
  );
};

export const getDeviceShortEdge = (targetWindow = window) => {
  const screenObject = targetWindow?.screen;
  const screenWidth = Number(screenObject?.width || 0);
  const screenHeight = Number(screenObject?.height || 0);

  if (screenWidth > 0 && screenHeight > 0) {
    return Math.min(screenWidth, screenHeight);
  }

  const innerWidth = Number(targetWindow?.innerWidth || 0);
  const innerHeight = Number(targetWindow?.innerHeight || 0);
  return Math.min(innerWidth, innerHeight);
};

export const isTabletDevice = (targetWindow = window) => {
  if (!isTouchDevice(targetWindow)) {
    return false;
  }

  return isProbablyIpad(targetWindow) || getDeviceShortEdge(targetWindow) >= 700;
};

export const getCurrentOrientation = (targetWindow = window) => {
  const orientationType = targetWindow?.screen?.orientation?.type;
  if (typeof orientationType === "string") {
    return orientationType.includes("landscape") ? "landscape" : "portrait";
  }

  const viewportWidth =
    Number(targetWindow?.visualViewport?.width || 0) || Number(targetWindow?.innerWidth || 0);
  const viewportHeight =
    Number(targetWindow?.visualViewport?.height || 0) || Number(targetWindow?.innerHeight || 0);

  return viewportWidth > viewportHeight ? "landscape" : "portrait";
};

export const getRequiredOrientation = (targetWindow = window) => {
  if (!isTouchDevice(targetWindow)) {
    return null;
  }

  // iPad supports both orientations; portrait uses the phone navigation layout.
  if (isProbablyIpad(targetWindow)) return null;

  return isTabletDevice(targetWindow) ? "landscape" : "portrait";
};

export const shouldShowOrientationOverlay = (targetWindow = window) => {
  const requiredOrientation = getRequiredOrientation(targetWindow);
  if (!requiredOrientation) {
    return false;
  }

  return getCurrentOrientation(targetWindow) !== requiredOrientation;
};
